// Watch destinations: broadcast outlet name -> official watch page.
//
// This table is the single place where Watch buttons get their URLs
// (Watch Links plan, Part A). Today these are the services' plain
// official links. As affiliate approvals land (Amazon Associates,
// FlexOffers, Impact, ...), the tracked link for a service replaces the
// plain URL right here — no UI changes needed anywhere else.
//
// An outlet with no entry still renders as a text listing (the plan's
// rule: no program != no button only when we know the destination;
// an unknown outlet is shown as information, never guessed at).

const DESTINATIONS = {
  // National networks and their own streaming homes
  'espn': 'https://www.espn.com/watch',
  'espn2': 'https://www.espn.com/watch',
  'espnu': 'https://www.espn.com/watch',
  'espnews': 'https://www.espn.com/watch',
  'espn app': 'https://www.espn.com/watch',
  'espn plus': 'https://plus.espn.com',
  'abc': 'https://abc.com',
  'cbs': 'https://www.paramountplus.com',
  'cbs sports network': 'https://www.cbssports.com/watch',
  'cbssn': 'https://www.cbssports.com/watch',
  'fox': 'https://www.foxsports.com/live',
  'fs1': 'https://www.foxsports.com/live',
  'fs2': 'https://www.foxsports.com/live',
  'fox sports app': 'https://www.foxsports.com/live',
  'nbc': 'https://www.peacocktv.com',
  'usa network': 'https://www.peacocktv.com',
  'usa net': 'https://www.peacocktv.com',
  'golf channel': 'https://www.peacocktv.com',
  'tnt': 'https://www.max.com/sports',
  'tbs': 'https://www.max.com/sports',
  'trutv': 'https://www.max.com/sports',
  'the cw': 'https://www.cwtv.com',
  'ion': 'https://iontelevision.com',

  // Streamers and league passes
  'peacock': 'https://www.peacocktv.com',
  'paramount plus': 'https://www.paramountplus.com',
  'prime video': 'https://www.primevideo.com',
  'amazon prime video': 'https://www.primevideo.com',
  'apple tv plus': 'https://tv.apple.com',
  'max': 'https://www.max.com',
  'hulu': 'https://www.hulu.com',
  'hulu plus live tv': 'https://www.hulu.com/live',
  'netflix': 'https://www.netflix.com',
  'disney plus': 'https://www.disneyplus.com',
  'nba league pass': 'https://www.nba.com/league-pass',
  'nba tv': 'https://www.nba.com/watch',
  'mlb tv': 'https://www.mlb.com/tv',
  'mlb network': 'https://www.mlb.com/tv',
  'mlb net': 'https://www.mlb.com/tv',
  'nfl plus': 'https://www.nfl.com/plus',
  'nfl network': 'https://www.nfl.com/plus',
  'nfl net': 'https://www.nfl.com/plus',
  'nhl network': 'https://plus.espn.com',
  'nhl net': 'https://plus.espn.com',
  'fubo': 'https://www.fubo.tv',
  'fubotv': 'https://www.fubo.tv',
  'sling tv': 'https://www.sling.com',
  'sling': 'https://www.sling.com',
  'youtube tv': 'https://tv.youtube.com',
  'directv stream': 'https://www.directv.com/stream',
  'dazn': 'https://www.dazn.com',
  'tennis channel': 'https://www.tennischannel.com',
  'bein sports': 'https://www.beinsports.com',
  'telemundo': 'https://www.peacocktv.com',
  'universo': 'https://www.peacocktv.com',
  'fox deportes': 'https://www.foxsports.com/live',
  'espn deportes': 'https://www.espn.com/watch',

  // Regional sports networks and team apps (plain links; most have no
  // public affiliate program — SNY and Marquee also sell as Prime add-ons)
  'msg': 'https://gothamsports.com',
  'msg network': 'https://gothamsports.com',
  'yes': 'https://gothamsports.com',
  'yes network': 'https://gothamsports.com',
  'sny': 'https://www.sny.tv',
  'nesn': 'https://nesn.com',
  'marquee': 'https://watchmarquee.com',
  'marquee sports network': 'https://watchmarquee.com',
  'monumental sports': 'https://www.monumentalsports.com',
  'monumental': 'https://www.monumentalsports.com',
  'fanduel sports network': 'https://fanduelsportsnetwork.com',
  'bally sports': 'https://fanduelsportsnetwork.com',
  'spectrum sportsnet': 'https://spectrumsportsnet.com',
  'altitude': 'https://www.altitudesports.com',
  'altitude sports': 'https://www.altitudesports.com',
  'root sports': 'https://www.rootsports.com',
  'nbc sports boston': 'https://www.nbcsportsboston.com',
  'nbc sports chicago': 'https://www.nbcsportschicago.com',
  'nbc sports philadelphia': 'https://www.nbcsportsphiladelphia.com',
  'nbc sports california': 'https://www.nbcsportsbayarea.com',
  'nbc sports bay area': 'https://www.nbcsportsbayarea.com',
  'space city home network': 'https://www.spacecityhn.com',
};

const normalizeOutlet = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/\+/g, ' plus ')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const NORMALIZED = {};
Object.keys(DESTINATIONS).forEach((key) => {
  NORMALIZED[normalizeOutlet(key)] = DESTINATIONS[key];
});

/** Official watch URL for an outlet name, or null when unmapped. */
export const resolveWatchDestination = (name) =>
  NORMALIZED[normalizeOutlet(name)] || null;

// Shown wherever Watch buttons appear (Watch Links plan, Part B rules).
export const WATCH_DISCLOSURE =
  "Paid links: we may earn a commission if you subscribe — your price doesn't change.";
